import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { 
  MessageSquare, 
  Calendar, 
  Mail, 
  Phone, 
  User, 
  Home,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Search,
  Eye,
  RefreshCw
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Inquiries = ({ isAdmin = false }) => {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    new: 0,
    contacted: 0,
    converted: 0
  });

  useEffect(() => {
    fetchInquiries();
  }, []);

  const fetchInquiries = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/inquiries/`);
      const data = response.data;
      setInquiries(data);
      
      // Calculate stats
      setStats({
        total: data.length,
        new: data.filter(i => i.status === 'new' || i.status === 'pending').length,
        contacted: data.filter(i => i.status === 'contacted').length,
        converted: data.filter(i => i.status === 'converted').length
      });
    } catch (error) {
      console.error('Failed to fetch inquiries:', error);
      toast.error('Failed to load inquiries');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (inquiryId, newStatus) => {
    try {
      await axios.put(`${API}/inquiries/${inquiryId}/status?status=${newStatus}`);
      toast.success('Status updated successfully');
      fetchInquiries();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      new: 'bg-blue-500 text-white',
      pending: 'bg-yellow-500 text-white',
      contacted: 'bg-purple-500 text-white',
      converted: 'bg-green-600 text-white',
      closed: 'bg-gray-500 text-white'
    };
    return styles[status] || 'bg-gray-400 text-white';
  };

  const getTypeBadge = (type) => {
    return type === 'contact' 
      ? 'bg-brand-brown text-white' 
      : 'bg-brand-gold text-brand-brown';
  };

  const filteredInquiries = inquiries.filter(inquiry => {
    // Filter by tab
    if (activeTab === 'contact' && inquiry.type !== 'contact') return false;
    if (activeTab === 'schedule' && inquiry.type !== 'schedule') return false;
    if (activeTab === 'new' && inquiry.status !== 'new' && inquiry.status !== 'pending') return false;
    
    // Filter by search
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return (
        inquiry.name?.toLowerCase().includes(search) ||
        inquiry.email?.toLowerCase().includes(search) ||
        inquiry.phone?.includes(search) ||
        inquiry.listing_title?.toLowerCase().includes(search)
      );
    }
    return true;
  });

  const viewDetails = (inquiry) => {
    setSelectedInquiry(inquiry);
    setDetailDialogOpen(true);
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-foreground">
            Inquiries
          </h1>
          <p className="font-body text-muted-foreground mt-1">
            Manage contact requests and viewing schedules from visitors
          </p>
        </div>
        <Button 
          onClick={fetchInquiries} 
          variant="outline" 
          className="rounded-none border-2"
          data-testid="refresh-inquiries-btn"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="rounded-none border-border">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-2">
              <MessageSquare className="w-4 h-4" />
              Total Inquiries
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="font-heading text-3xl font-bold" data-testid="stat-total">
              {stats.total}
            </p>
          </CardContent>
        </Card>
        <Card className="rounded-none border-border">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              New / Pending
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="font-heading text-3xl font-bold text-blue-600" data-testid="stat-new">
              {stats.new}
            </p>
          </CardContent>
        </Card>
        <Card className="rounded-none border-border">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-2">
              <Phone className="w-4 h-4" />
              Contacted
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="font-heading text-3xl font-bold text-purple-600" data-testid="stat-contacted">
              {stats.contacted}
            </p>
          </CardContent>
        </Card>
        <Card className="rounded-none border-border">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              Converted
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="font-heading text-3xl font-bold text-green-600" data-testid="stat-converted">
              {stats.converted}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1">
          <TabsList className="bg-secondary rounded-none p-1">
            <TabsTrigger value="all" className="rounded-none font-heading uppercase tracking-wide text-xs" data-testid="tab-all">
              All
            </TabsTrigger>
            <TabsTrigger value="new" className="rounded-none font-heading uppercase tracking-wide text-xs" data-testid="tab-new">
              New
            </TabsTrigger>
            <TabsTrigger value="contact" className="rounded-none font-heading uppercase tracking-wide text-xs" data-testid="tab-contact">
              <MessageSquare className="w-4 h-4 mr-1" />
              Contact
            </TabsTrigger>
            <TabsTrigger value="schedule" className="rounded-none font-heading uppercase tracking-wide text-xs" data-testid="tab-schedule">
              <Calendar className="w-4 h-4 mr-1" />
              Schedule
            </TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="relative w-full sm:w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search inquiries..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="rounded-none border-2 pl-10"
            data-testid="search-inquiries"
          />
        </div>
      </div>

      {/* Inquiries Table */}
      <Card className="rounded-none border-border">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-secondary/50">
                <TableHead className="font-heading text-xs uppercase tracking-widest">Type</TableHead>
                <TableHead className="font-heading text-xs uppercase tracking-widest">Contact</TableHead>
                <TableHead className="font-heading text-xs uppercase tracking-widest">Property</TableHead>
                <TableHead className="font-heading text-xs uppercase tracking-widest">Date</TableHead>
                <TableHead className="font-heading text-xs uppercase tracking-widest">Status</TableHead>
                <TableHead className="font-heading text-xs uppercase tracking-widest text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-12">
                    <div className="w-8 h-8 border-4 border-brand-gold border-t-transparent rounded-full animate-spin mx-auto"></div>
                  </TableCell>
                </TableRow>
              ) : filteredInquiries.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-12">
                    <MessageSquare className="w-12 h-12 text-muted-foreground mx-auto mb-2" />
                    <p className="font-body text-muted-foreground">No inquiries found</p>
                  </TableCell>
                </TableRow>
              ) : (
                filteredInquiries.map((inquiry) => (
                  <TableRow key={inquiry.id} className="hover:bg-secondary/30" data-testid={`inquiry-row-${inquiry.id}`}>
                    <TableCell>
                      <Badge className={`rounded-none font-heading text-[10px] uppercase tracking-widest ${getTypeBadge(inquiry.type)}`}>
                        {inquiry.type === 'contact' ? (
                          <><MessageSquare className="w-3 h-3 mr-1" /> Contact</>
                        ) : (
                          <><Calendar className="w-3 h-3 mr-1" /> Viewing</>
                        )}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        <p className="font-heading font-semibold text-sm flex items-center gap-1">
                          <User className="w-3 h-3" />
                          {inquiry.name}
                        </p>
                        <p className="font-body text-xs text-muted-foreground flex items-center gap-1">
                          <Mail className="w-3 h-3" />
                          {inquiry.email}
                        </p>
                        {inquiry.phone && (
                          <p className="font-body text-xs text-muted-foreground flex items-center gap-1">
                            <Phone className="w-3 h-3" />
                            {inquiry.phone}
                          </p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {inquiry.listing_title ? (
                        <div className="flex items-center gap-1">
                          <Home className="w-4 h-4 text-brand-gold" />
                          <span className="font-body text-sm truncate max-w-[150px]" title={inquiry.listing_title}>
                            {inquiry.listing_title}
                          </span>
                        </div>
                      ) : (
                        <span className="text-muted-foreground text-sm">General inquiry</span>
                      )}
                      {inquiry.preferred_date && (
                        <p className="font-body text-xs text-muted-foreground mt-1 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {inquiry.preferred_date} at {inquiry.preferred_time}
                        </p>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="font-body text-sm text-muted-foreground">
                        {formatDate(inquiry.created_at)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Select 
                        value={inquiry.status} 
                        onValueChange={(value) => updateStatus(inquiry.id, value)}
                      >
                        <SelectTrigger className="rounded-none border-0 w-[120px] h-8 p-0" data-testid={`status-select-${inquiry.id}`}>
                          <Badge className={`rounded-none font-heading text-[10px] uppercase tracking-widest ${getStatusBadge(inquiry.status)}`}>
                            {inquiry.status}
                          </Badge>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="new">New</SelectItem>
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="contacted">Contacted</SelectItem>
                          <SelectItem value="converted">Converted</SelectItem>
                          <SelectItem value="closed">Closed</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => viewDetails(inquiry)}
                        className="rounded-none"
                        data-testid={`view-inquiry-${inquiry.id}`}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-lg" aria-describedby="inquiry-detail-description">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold flex items-center gap-2">
              {selectedInquiry?.type === 'contact' ? (
                <><MessageSquare className="w-5 h-5 text-brand-brown" /> Contact Inquiry</>
              ) : (
                <><Calendar className="w-5 h-5 text-brand-gold" /> Viewing Request</>
              )}
            </DialogTitle>
            <p id="inquiry-detail-description" className="sr-only">
              Details of the selected inquiry
            </p>
          </DialogHeader>
          
          {selectedInquiry && (
            <div className="space-y-6 mt-4">
              {/* Contact Info */}
              <div className="space-y-3">
                <h4 className="font-heading text-xs uppercase tracking-widest font-bold text-muted-foreground">
                  Contact Information
                </h4>
                <div className="bg-secondary/30 p-4 space-y-2">
                  <p className="font-body flex items-center gap-2">
                    <User className="w-4 h-4 text-brand-gold" />
                    <span className="font-semibold">{selectedInquiry.name}</span>
                  </p>
                  <p className="font-body flex items-center gap-2">
                    <Mail className="w-4 h-4 text-brand-gold" />
                    <a href={`mailto:${selectedInquiry.email}`} className="hover:underline text-brand-brown">
                      {selectedInquiry.email}
                    </a>
                  </p>
                  {selectedInquiry.phone && (
                    <p className="font-body flex items-center gap-2">
                      <Phone className="w-4 h-4 text-brand-gold" />
                      <a href={`tel:${selectedInquiry.phone}`} className="hover:underline">
                        {selectedInquiry.phone}
                      </a>
                    </p>
                  )}
                </div>
              </div>

              {/* Property Info */}
              {selectedInquiry.listing_title && (
                <div className="space-y-3">
                  <h4 className="font-heading text-xs uppercase tracking-widest font-bold text-muted-foreground">
                    Property of Interest
                  </h4>
                  <div className="bg-secondary/30 p-4">
                    <p className="font-body flex items-center gap-2">
                      <Home className="w-4 h-4 text-brand-gold" />
                      <span className="font-semibold">{selectedInquiry.listing_title}</span>
                    </p>
                  </div>
                </div>
              )}

              {/* Preferred Viewing Time */}
              {selectedInquiry.preferred_date && (
                <div className="space-y-3">
                  <h4 className="font-heading text-xs uppercase tracking-widest font-bold text-muted-foreground">
                    Preferred Viewing Time
                  </h4>
                  <div className="bg-secondary/30 p-4">
                    <p className="font-body flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-brand-gold" />
                      <span>{selectedInquiry.preferred_date}</span>
                    </p>
                    <p className="font-body flex items-center gap-2 mt-1">
                      <Clock className="w-4 h-4 text-brand-gold" />
                      <span>{selectedInquiry.preferred_time}</span>
                    </p>
                  </div>
                </div>
              )}

              {/* Message */}
              {selectedInquiry.message && (
                <div className="space-y-3">
                  <h4 className="font-heading text-xs uppercase tracking-widest font-bold text-muted-foreground">
                    Message
                  </h4>
                  <div className="bg-secondary/30 p-4">
                    <p className="font-body text-sm whitespace-pre-wrap">{selectedInquiry.message}</p>
                  </div>
                </div>
              )}

              {/* Status & Actions */}
              <div className="flex items-center justify-between pt-4 border-t border-border">
                <div className="flex items-center gap-2">
                  <span className="font-heading text-xs uppercase tracking-widest text-muted-foreground">Status:</span>
                  <Select 
                    value={selectedInquiry.status} 
                    onValueChange={(value) => {
                      updateStatus(selectedInquiry.id, value);
                      setSelectedInquiry({ ...selectedInquiry, status: value });
                    }}
                  >
                    <SelectTrigger className="rounded-none border-2 w-[140px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">New</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="contacted">Contacted</SelectItem>
                      <SelectItem value="converted">Converted</SelectItem>
                      <SelectItem value="closed">Closed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <span className="font-body text-xs text-muted-foreground">
                  Received: {formatDate(selectedInquiry.created_at)}
                </span>
              </div>

              {/* Quick Actions */}
              <div className="flex gap-4">
                <Button
                  asChild
                  className="flex-1 rounded-none font-heading font-bold uppercase tracking-wide bg-brand-gold text-brand-brown hover:bg-brand-gold/90"
                >
                  <a href={`mailto:${selectedInquiry.email}?subject=Re: Your inquiry about ${selectedInquiry.listing_title || 'our properties'}`}>
                    <Mail className="w-4 h-4 mr-2" />
                    Send Email
                  </a>
                </Button>
                {selectedInquiry.phone && (
                  <Button
                    asChild
                    variant="outline"
                    className="flex-1 rounded-none border-2 font-heading font-bold uppercase tracking-wide"
                  >
                    <a href={`tel:${selectedInquiry.phone}`}>
                      <Phone className="w-4 h-4 mr-2" />
                      Call
                    </a>
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Inquiries;
